import { canHaveParent, type NodeType } from '@goalbuddy/core';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useStructure } from '@/lib/hooks';
import type { NewNodeType } from '@/lib/ui-events';
import { Button, Dialog, Input, Label, Select } from './ui';

const LABEL: Record<NewNodeType, string> = { area: 'area', goal: 'goal', list: 'list' };

/** Global dialog to create an area, goal or list. Open with `openNewNode(type, parentId?)`. */
export function NewNodeDialog() {
  const [type, setType] = useState<NewNodeType | null>(null);
  const [title, setTitle] = useState('');
  const [parent, setParent] = useState('');
  const { data: structure } = useStructure();
  const navigate = useNavigate();
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<{ type: NewNodeType; parentId: number | null }>).detail;
      setType(d.type);
      setTitle('');
      setParent(d.parentId ? String(d.parentId) : '');
    };
    window.addEventListener('gb:new-node', onOpen);
    return () => window.removeEventListener('gb:new-node', onOpen);
  }, []);
  const parents = type
    ? (structure ?? []).filter(
        (p) => ['area', 'goal'].includes(p.type) && canHaveParent(type as NodeType, p.type as NodeType),
      )
    : [];
  const create = useMutation({
    mutationFn: () =>
      api.createNode({ type: type!, title: title.trim(), parent_id: parent ? Number(parent) : null }),
    onSuccess: (n) => {
      setType(null);
      navigate(`/node/${n.id}`);
    },
  });
  return (
    <Dialog
      open={type !== null}
      onOpenChange={(o) => !o && setType(null)}
      title={type ? `New ${LABEL[type]}` : ''}
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="nn-title">Name</Label>
          <Input
            id="nn-title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={
              type === 'list'
                ? 'e.g. Gadget wishlist'
                : type === 'goal'
                  ? 'What do you want to achieve?'
                  : 'e.g. Health'
            }
          />
        </div>
        {type !== 'area' && (
          <div className="space-y-1.5">
            <Label htmlFor="nn-parent">Belongs to</Label>
            <Select id="nn-parent" value={parent} onChange={(e) => setParent(e.target.value)}>
              <option value="">(top level)</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.type}: {p.title}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={() => setType(null)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!title.trim() || create.isPending}>
            Create {type}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
